import { useState, useEffect, useRef, Fragment } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import ThemeToggle from "./ThemeToggle";
import logoVerticalTagline from "@/assets/logo-vertical-tagline-v2.png.asset.json";
import { useB2bProductsQuery } from "@/hooks/useB2bProductsQuery";

const limeFilter = 'brightness(0) saturate(100%) invert(89%) sepia(47%) saturate(1000%) hue-rotate(22deg) brightness(104%) contrast(99%)';
const purpleFilter = 'brightness(0) saturate(100%) invert(30%) sepia(69%) saturate(2151%) hue-rotate(228deg) brightness(92%) contrast(88%)';

const Navbar = () => {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const [b2bOpen, setB2bOpen] = useState(false);
  const [mobileB2bOpen, setMobileB2bOpen] = useState(false);
  const b2bRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);
  const { products: b2bProducts } = useB2bProductsQuery();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);

    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    setIsDark(document.documentElement.classList.contains("dark"));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!b2bOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setB2bOpen(false); };
    const onClick = (e: MouseEvent) => {
      if (b2bRef.current && !b2bRef.current.contains(e.target as Node)) setB2bOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [b2bOpen]);

  const openB2b = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setB2bOpen(true);
  };
  const scheduleCloseB2b = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setB2bOpen(false), 120);
  };

  const hasB2b = b2bProducts.length > 0;

  const links = [
    { label: "Cursos", href: "/cursos" },
    { label: "Formadores", href: "/#formadores" },
    { label: "Materiais", href: "/materiais" },
    { label: "Blog", href: "/blog" },
    { label: "FAQ", href: "/#faq" },
  ];

  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "glass" : "bg-transparent"}`}
    >
      <div className="container mx-auto flex items-center justify-between py-4 px-6">
        <a href="/">
          <img
            src={logoVerticalTagline.url}
            alt="Emera"
            className="h-12 md:h-20 w-auto transition-all duration-300"
            style={isHome ? ((!scrolled || isDark) ? { filter: "brightness(0) invert(1)" } : { filter: "brightness(0)" }) : ((!scrolled || isDark) ? { filter: purpleFilter } : { filter: purpleFilter })}
          />
        </a>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-8">
          {links.map((link) => (
            <Fragment key={link.label}>
              <a
                href={link.href}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {link.label}
              </a>
              {/* Insert "Para empresas" right after Cursos */}
              {link.label === "Cursos" && hasB2b && (
                <div
                  ref={b2bRef}
                  className="relative"
                  onMouseEnter={openB2b}
                  onMouseLeave={scheduleCloseB2b}
                >
                  <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={b2bOpen}
                    onClick={() => setB2bOpen((v) => !v)}
                    className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
                  >
                    Para empresas
                    <ChevronDown size={14} className={`transition-transform ${b2bOpen ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {b2bOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15 }}
                        className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-[300px] glass rounded-xl border border-border shadow-lg overflow-hidden"
                        role="menu"
                      >
                        <a
                          href="/b2b"
                          className="block px-4 py-3 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors border-b border-border/60"
                          role="menuitem"
                          onClick={() => setB2bOpen(false)}
                        >
                          Visão geral
                          <span className="block text-xs font-normal text-muted-foreground mt-0.5">Programas Emera para empresas</span>
                        </a>
                        <div className="py-1">
                          {b2bProducts.map((p) => (
                            <a
                              key={p.slug}
                              href={`/b2b/${p.slug}`}
                              className="block px-4 py-2.5 text-sm text-foreground hover:bg-muted/50 transition-colors"
                              role="menuitem"
                              onClick={() => setB2bOpen(false)}
                            >
                              {p.title}
                              {p.tagline && (
                                <span className="block text-xs text-muted-foreground mt-0.5 line-clamp-1">{p.tagline}</span>
                              )}
                            </a>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </Fragment>
          ))}
          <ThemeToggle />
          <a
            href="/#cursos"
            className="bg-primary text-primary-foreground px-6 py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            Começar Agora
          </a>
        </div>

        {/* Mobile toggle */}
        <div className="flex items-center gap-2 md:hidden">
          <ThemeToggle />
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="text-foreground"
            aria-label={isOpen ? "Fechar menu" : "Abrir menu"}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden glass overflow-hidden"
          >
            <div className="flex flex-col gap-4 px-6 py-6">
              {links.map((link) => (
                <div key={link.label}>
                  <a
                    href={link.href}
                    onClick={() => setIsOpen(false)}
                    className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </a>
                  {link.label === "Cursos" && hasB2b && (
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={() => setMobileB2bOpen((v) => !v)}
                        aria-expanded={mobileB2bOpen}
                        className="w-full flex items-center justify-between text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                      >
                        Para empresas
                        <ChevronDown size={14} className={`transition-transform ${mobileB2bOpen ? "rotate-180" : ""}`} />
                      </button>
                      <AnimatePresence>
                        {mobileB2bOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="pl-3 mt-3 flex flex-col gap-3 border-l border-border">
                              <a
                                href="/b2b"
                                onClick={() => setIsOpen(false)}
                                className="text-sm text-foreground"
                              >
                                Visão geral
                              </a>
                              {b2bProducts.map((p) => (
                                <a
                                  key={p.slug}
                                  href={`/b2b/${p.slug}`}
                                  onClick={() => setIsOpen(false)}
                                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                                >
                                  {p.title}
                                </a>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
              ))}
              <a
                href="/#cursos"
                onClick={() => setIsOpen(false)}
                className="bg-primary text-primary-foreground px-6 py-2.5 rounded-lg text-sm font-semibold text-center hover:opacity-90 transition-opacity"
              >
                Começar Agora
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
